const BRAND_WATERMARK = "拼豆物语";

export function renderBeadArtwork(canvas, options = {}) {
  const pixels = Array.isArray(options.pixels) ? options.pixels : [];
  const gridWidth = Math.max(1, Number(options.gridWidth) || 1);
  const gridHeight = Math.max(1, Number(options.gridHeight) || 1);
  const mode = options.mode === "3d" ? "3d" : "2d";
  const cell = Math.max(8, Number(options.cell) || (mode === "3d" ? 18 : 16));
  const bounds = options.crop === false ? fullBounds(gridWidth, gridHeight) : occupiedBounds(pixels, gridWidth, gridHeight);
  const marginCells = Math.max(1, Number(options.marginCells) || 2);
  const minX = Math.max(0, bounds.minX - marginCells);
  const minY = Math.max(0, bounds.minY - marginCells);
  const maxX = Math.min(gridWidth - 1, bounds.maxX + marginCells);
  const maxY = Math.min(gridHeight - 1, bounds.maxY + marginCells);
  const columns = maxX - minX + 1;
  const rows = maxY - minY + 1;
  const outer = Math.max(18, Math.round(cell * 1.5));
  canvas.width = columns * cell + outer * 2;
  canvas.height = rows * cell + outer * 2;

  const context = canvas.getContext("2d");
  context.clearRect(0, 0, canvas.width, canvas.height);
  if (!options.transparent) {
    context.fillStyle = options.background || "#fffaf4";
    context.fillRect(0, 0, canvas.width, canvas.height);
  }

  if (options.showPegboard) drawPegboard(context, columns, rows, cell, outer);

  for (let y = minY; y <= maxY; y += 1) {
    for (let x = minX; x <= maxX; x += 1) {
      const pixel = pixels[y * gridWidth + x];
      if (!pixel || pixel.empty || !pixel.code) continue;
      const rgb = Array.isArray(pixel.rgb) ? pixel.rgb : [190, 190, 190];
      const cx = outer + (x - minX) * cell + cell / 2;
      const cy = outer + (y - minY) * cell + cell / 2;
      if (mode === "3d") drawBead(context, cx, cy, cell, rgb, options.finish || "soft");
      else drawPixel(context, cx, cy, cell, rgb);
    }
  }

  if (options.watermark !== false) {
    applyTiledWatermark(context, canvas.width, canvas.height, options.watermarkText || BRAND_WATERMARK);
  }
  return canvas;
}

export async function renderImageBeadArtwork(canvas, imageSource, options = {}) {
  if (!canvas || !imageSource) throw new Error("缺少 3D 转换所需的作品图片");
  const image = await loadArtworkImage(imageSource);
  const grid = Math.max(24, Math.min(64, Number(options.grid) || 42));
  const ratio = image.naturalWidth / Math.max(1, image.naturalHeight);
  const columns = ratio >= 1 ? grid : Math.max(20, Math.round(grid * ratio));
  const rows = ratio >= 1 ? Math.max(20, Math.round(grid / ratio)) : grid;
  const source = document.createElement("canvas");
  source.width = columns;
  source.height = rows;
  const sourceContext = source.getContext("2d", {willReadFrequently: true});
  sourceContext.imageSmoothingEnabled = true;
  sourceContext.imageSmoothingQuality = "high";
  sourceContext.drawImage(image, 0, 0, columns, rows);
  const sampled = sourceContext.getImageData(0, 0, columns, rows).data;
  const cell = Math.max(10, Number(options.cell) || 18);
  const outer = Math.max(22, Math.round(cell * 1.55));
  canvas.width = columns * cell + outer * 2;
  canvas.height = rows * cell + outer * 2;
  const context = canvas.getContext("2d");
  context.clearRect(0, 0, canvas.width, canvas.height);
  if (!options.transparent) {
    context.fillStyle = options.background || "#f7f3ec";
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  if (options.showPegboard) drawPegboard(context, columns, rows, cell, outer);
  for (let y = 0; y < rows; y += 1) {
    for (let x = 0; x < columns; x += 1) {
      const index = (y * columns + x) * 4;
      const color = [sampled[index], sampled[index + 1], sampled[index + 2]];
      const alpha = sampled[index + 3];
      if (alpha < 80 || isNearWhite(color)) continue;
      drawBead(
        context,
        outer + x * cell + cell / 2,
        outer + y * cell + cell / 2,
        cell,
        color,
        options.finish || "raw"
      );
    }
  }
  if (options.watermark !== false) {
    applyTiledWatermark(context, canvas.width, canvas.height, options.watermarkText || BRAND_WATERMARK);
  }
  return canvas;
}

export function createBeadArtworkDataUrl(options = {}) {
  const canvas = document.createElement("canvas");
  renderBeadArtwork(canvas, options);
  return canvas.toDataURL("image/png");
}

export async function createWatermarkedImageDataUrl(imageSource, options = {}) {
  if (!imageSource) return "";
  const image = await loadArtworkImage(imageSource);
  const maxSide = Math.max(640, Number(options.maxSide) || 1600);
  const ratio = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio));
  const context = canvas.getContext("2d");
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  applyTiledWatermark(context, canvas.width, canvas.height, options.watermarkText || BRAND_WATERMARK);
  return canvas.toDataURL("image/jpeg", .9);
}

export async function createCartoonPreviewDataUrl(imageSource, options = {}) {
  if (!imageSource) return "";
  const image = await loadArtworkImage(imageSource);
  const maxSide = Math.max(640, Number(options.maxSide) || 1200);
  const ratio = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio));
  const context = canvas.getContext("2d", {willReadFrequently: true});
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.filter = "saturate(1.12) contrast(1.06)";
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  context.filter = "none";
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const source = new Uint8ClampedArray(data);
  const step = 24;
  for (let index = 0; index < data.length; index += 4) {
    data[index] = Math.min(255, Math.round(data[index] / step) * step);
    data[index + 1] = Math.min(255, Math.round(data[index + 1] / step) * step);
    data[index + 2] = Math.min(255, Math.round(data[index + 2] / step) * step);
  }
  const width = canvas.width;
  const height = canvas.height;
  const luminanceAt = (x, y) => {
    const offset = (Math.max(0, Math.min(height - 1, y)) * width + Math.max(0, Math.min(width - 1, x))) * 4;
    return source[offset] * .299 + source[offset + 1] * .587 + source[offset + 2] * .114;
  };
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const gx = luminanceAt(x + 1, y) - luminanceAt(x - 1, y);
      const gy = luminanceAt(x, y + 1) - luminanceAt(x, y - 1);
      const strength = Math.abs(gx) + Math.abs(gy);
      if (strength < 62) continue;
      const offset = (y * width + x) * 4;
      const factor = strength > 125 ? .38 : .68;
      data[offset] *= factor;
      data[offset + 1] *= factor;
      data[offset + 2] *= factor;
    }
  }
  context.putImageData(imageData, 0, 0);
  applyTiledWatermark(context, canvas.width, canvas.height, options.watermarkText || BRAND_WATERMARK);
  return canvas.toDataURL("image/jpeg", .9);
}

export function createPatternDataUrl(options = {}) {
  const pixels = Array.isArray(options.pixels) ? options.pixels : [];
  const gridWidth = Math.max(1, Number(options.gridWidth) || 1);
  const gridHeight = Math.max(1, Number(options.gridHeight) || 1);
  const member = Boolean(options.member);
  const cell = member ? 28 : 18;
  const side = 34;
  const stats = buildStats(pixels);
  const columns = Math.min(4, Math.max(1, stats.length));
  const rows = Math.ceil(stats.length / columns);
  const patternWidth = gridWidth * cell;
  const patternHeight = gridHeight * cell;
  const canvas = document.createElement("canvas");
  canvas.width = patternWidth + side * 2;
  canvas.height = patternHeight + Math.max(120, rows * 42 + 64) + 104;
  const context = canvas.getContext("2d");
  context.fillStyle = "#fff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = "#211c1a";
  context.font = "800 26px \"Microsoft YaHei\", sans-serif";
  context.textAlign = "left";
  context.fillText("拼豆图纸", side, 38);
  context.fillStyle = "#716964";
  context.font = "14px \"Microsoft YaHei\", sans-serif";
  context.fillText(`${gridWidth} × ${gridHeight} · ${stats.reduce((sum, item) => sum + item.count, 0)} 颗 · ${stats.length} 色`, side, 66);
  pixels.forEach((pixel, index) => {
    const x = side + (index % gridWidth) * cell;
    const y = 84 + Math.floor(index / gridWidth) * cell;
    const color = Array.isArray(pixel?.rgb) ? pixel.rgb : [255, 255, 255];
    context.fillStyle = !pixel || pixel.empty || !pixel.code ? "#fff" : cssRgb(color);
    context.fillRect(x, y, cell, cell);
    context.strokeStyle = "rgba(40,45,43,.18)";
    context.strokeRect(x + 0.5, y + 0.5, cell - 1, cell - 1);
    if (pixel && !pixel.empty && pixel.code) {
      context.fillStyle = luminance(color) > 155 ? "#222" : "#fff";
      context.font = `800 ${Math.max(7, Math.round(cell * 0.36))}px Arial, sans-serif`;
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText(pixel.code, x + cell / 2, y + cell / 2);
    }
  });
  const legendTop = patternHeight + 116;
  context.textAlign = "left";
  context.textBaseline = "alphabetic";
  context.fillStyle = "#211c1a";
  context.font = "800 19px \"Microsoft YaHei\", sans-serif";
  context.fillText("颜色统计", side, legendTop);
  const columnWidth = patternWidth / columns;
  stats.forEach((item, index) => {
    const x = side + (index % columns) * columnWidth;
    const y = legendTop + 27 + Math.floor(index / columns) * 42;
    context.fillStyle = cssRgb(item.rgb);
    context.fillRect(x, y, 24, 24);
    context.fillStyle = "#393330";
    context.font = "13px \"Microsoft YaHei\", sans-serif";
    context.fillText(`${item.code} ${item.name || ""} × ${item.count}`, x + 32, y + 17);
  });
  if (!member) applyTiledWatermark(context, canvas.width, canvas.height);
  return canvas.toDataURL("image/png");
}

/**
 * 首页/作品卡片使用的紧凑识别图纸。
 * 与下载版图纸分开：这里强调主体、色号和定位网格，不保留大面积空白及文档标题。
 */
export function createPatternPreviewDataUrl(options = {}) {
  const pixels = Array.isArray(options.pixels) ? options.pixels : [];
  const gridWidth = Math.max(1, Number(options.gridWidth) || 1);
  const gridHeight = Math.max(1, Number(options.gridHeight) || 1);
  const rawBounds = occupiedBounds(pixels, gridWidth, gridHeight);
  const marginCells = Math.max(1, Number(options.marginCells) || 2);
  const minX = Math.max(0, rawBounds.minX - marginCells);
  const minY = Math.max(0, rawBounds.minY - marginCells);
  const maxX = Math.min(gridWidth - 1, rawBounds.maxX + marginCells);
  const maxY = Math.min(gridHeight - 1, rawBounds.maxY + marginCells);
  const columns = Math.max(1, maxX - minX + 1);
  const rows = Math.max(1, maxY - minY + 1);
  const cell = Math.max(15, Number(options.cell) || 18);
  const side = Math.max(15, Math.round(cell * 1.05));
  const legend = buildStats(pixels).slice(0, 24);
  const legendColumns = Math.min(8, Math.max(1, legend.length));
  const legendRows = Math.ceil(legend.length / legendColumns);
  const legendRowHeight = 26;
  const legendHeight = legend.length ? legendRows * legendRowHeight + 18 : 0;
  const boardWidth = columns * cell;
  const boardHeight = rows * cell;
  const canvas = document.createElement("canvas");
  canvas.width = boardWidth + side * 2;
  canvas.height = boardHeight + side * 2 + legendHeight;
  const context = canvas.getContext("2d");

  context.fillStyle = options.background || "#fffdf9";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = "#fff";
  context.fillRect(side, side, boardWidth, boardHeight);

  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const sourceX = minX + column;
      const sourceY = minY + row;
      const pixel = pixels[sourceY * gridWidth + sourceX];
      const x = side + column * cell;
      const y = side + row * cell;
      const filled = pixel && !pixel.empty && pixel.code;
      const color = Array.isArray(pixel?.rgb) ? pixel.rgb : [255, 255, 255];
      context.fillStyle = filled ? cssRgb(color) : "#fff";
      context.fillRect(x, y, cell, cell);
      context.strokeStyle = "rgba(55,49,46,.16)";
      context.lineWidth = 1;
      context.strokeRect(x + .5, y + .5, cell - 1, cell - 1);
      if (!filled) continue;
      context.fillStyle = luminance(color) > 158 ? "#1d1a19" : "#fff";
      context.font = `800 ${Math.max(7, Math.floor(cell * .36))}px Arial, sans-serif`;
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText(String(pixel.code), x + cell / 2, y + cell / 2 + .25);
    }
  }

  // 以原图坐标为准画每 10 格定位线，制作时更容易数格和拼接底板。
  context.save();
  context.strokeStyle = "rgba(224,72,72,.72)";
  context.lineWidth = Math.max(1.25, cell * .09);
  for (let sourceX = Math.ceil(minX / 10) * 10; sourceX <= maxX + 1; sourceX += 10) {
    const x = side + (sourceX - minX) * cell + .5;
    context.beginPath();
    context.moveTo(x, side);
    context.lineTo(x, side + boardHeight);
    context.stroke();
  }
  for (let sourceY = Math.ceil(minY / 10) * 10; sourceY <= maxY + 1; sourceY += 10) {
    const y = side + (sourceY - minY) * cell + .5;
    context.beginPath();
    context.moveTo(side, y);
    context.lineTo(side + boardWidth, y);
    context.stroke();
  }
  context.restore();

  if (legend.length) {
    const legendTop = side + boardHeight + 11;
    const itemWidth = boardWidth / legendColumns;
    legend.forEach((item, index) => {
      const x = side + (index % legendColumns) * itemWidth;
      const y = legendTop + Math.floor(index / legendColumns) * legendRowHeight;
      context.fillStyle = cssRgb(item.rgb);
      context.fillRect(x, y, 14, 14);
      context.strokeStyle = "rgba(45,39,36,.16)";
      context.strokeRect(x + .5, y + .5, 13, 13);
      context.fillStyle = "#292421";
      context.font = "800 10px Arial, sans-serif";
      context.textAlign = "left";
      context.textBaseline = "middle";
      context.fillText(`${item.code} ${item.count}`, x + 18, y + 7.5);
    });
  }

  if (options.watermark !== false) {
    applyTiledWatermark(context, canvas.width, side + boardHeight, options.watermarkText || BRAND_WATERMARK);
  }
  return canvas.toDataURL("image/png");
}

export function applyTiledWatermark(context, width, height, text = BRAND_WATERMARK) {
  if (!context || !width || !height) return;
  const fontSize = Math.max(14, Math.min(30, Math.round(Math.min(width, height) / 18)));
  const stepX = Math.max(150, fontSize * 7);
  const stepY = Math.max(105, fontSize * 4.5);
  context.save();
  context.globalAlpha = 0.16;
  context.fillStyle = "#5d5368";
  context.font = `800 ${fontSize}px "Microsoft YaHei", sans-serif`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.translate(width / 2, height / 2);
  context.rotate(-Math.PI / 7);
  for (let y = -height * 1.2; y <= height * 1.2; y += stepY) {
    for (let x = -width * 1.2; x <= width * 1.2; x += stepX) context.fillText(text, x, y);
  }
  context.restore();
}

function occupiedBounds(pixels, width, height) {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  pixels.forEach((pixel, index) => {
    if (!pixel || pixel.empty || !pixel.code) return;
    const x = index % width;
    const y = Math.floor(index / width);
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  });
  return maxX < 0 ? fullBounds(width, height) : { minX, minY, maxX, maxY };
}

function fullBounds(width, height) {
  return { minX: 0, minY: 0, maxX: width - 1, maxY: height - 1 };
}

function drawPixel(context, cx, cy, cell, color) {
  const gap = Math.max(0.7, cell * 0.045);
  context.fillStyle = cssRgb(color);
  context.fillRect(cx - cell / 2 + gap, cy - cell / 2 + gap, cell - gap * 2, cell - gap * 2);
}

function drawBead(context, cx, cy, cell, color, finish) {
  const radius = cell * (finish === "flat" ? 0.49 : finish === "raw" ? 0.43 : 0.46);
  const dark = color.map(value => Math.max(0, value - 52));
  const midDark = color.map(value => Math.max(0, value - 24));
  const light = color.map(value => Math.min(255, value + 72));
  context.save();
  context.shadowColor = "rgba(43,31,21,.32)";
  context.shadowBlur = Math.max(1.2, cell * 0.16);
  context.shadowOffsetY = Math.max(1, cell * 0.12);
  context.fillStyle = cssRgb(dark);
  context.beginPath();
  context.ellipse(cx, cy + cell * 0.075, radius, radius * 0.96, 0, 0, Math.PI * 2);
  context.fill();
  context.shadowColor = "transparent";
  const gradient = context.createRadialGradient(cx - radius * 0.34, cy - radius * 0.42, radius * .05, cx, cy, radius);
  gradient.addColorStop(0, "rgba(255,255,255,.96)");
  gradient.addColorStop(.16, cssRgb(light));
  gradient.addColorStop(0.53, cssRgb(color));
  gradient.addColorStop(1, cssRgb(midDark));
  context.fillStyle = gradient;
  context.beginPath();
  context.arc(cx, cy, radius * 0.94, 0, Math.PI * 2);
  context.fill();
  if (finish !== "flat") {
    const holeRadius = radius * (finish === "raw" ? 0.29 : 0.105);
    context.fillStyle = finish === "raw" ? cssRgb(dark) : "rgba(25,18,15,.32)";
    context.beginPath();
    context.arc(cx, cy, holeRadius, 0, Math.PI * 2);
    context.fill();
    if (finish === "raw") {
      const hole = context.createRadialGradient(cx - holeRadius * .25, cy - holeRadius * .3, 0, cx, cy, holeRadius);
      hole.addColorStop(0, "rgba(255,255,255,.72)");
      hole.addColorStop(.42, "rgba(238,231,218,.94)");
      hole.addColorStop(.68, "rgba(91,72,56,.62)");
      hole.addColorStop(1, "rgba(35,27,22,.82)");
      context.fillStyle = hole;
      context.beginPath();
      context.arc(cx, cy, holeRadius * .72, 0, Math.PI * 2);
      context.fill();
      context.strokeStyle = "rgba(255,255,255,.72)";
      context.lineWidth = Math.max(0.7, cell * 0.04);
      context.beginPath();
      context.arc(cx, cy, radius * 0.76, Math.PI * 1.05, Math.PI * 1.72);
      context.stroke();
    }
  }
  context.restore();
}

function drawPegboard(context, columns, rows, cell, outer) {
  const x = outer - cell * 0.42;
  const y = outer - cell * 0.42;
  const width = columns * cell + cell * 0.84;
  const height = rows * cell + cell * 0.84;
  context.save();
  const radius = Math.max(8, cell * .65);
  context.shadowColor = "rgba(67,52,37,.28)";
  context.shadowBlur = Math.max(7, cell * 0.9);
  context.shadowOffsetY = Math.max(3, cell * 0.32);
  roundedRectPath(context, x, y, width, height, radius);
  const boardGradient = context.createLinearGradient(x, y, x + width, y + height);
  boardGradient.addColorStop(0, "#fffefb");
  boardGradient.addColorStop(.48, "#eee9df");
  boardGradient.addColorStop(1, "#d9d0c1");
  context.fillStyle = boardGradient;
  context.fill();
  context.shadowColor = "transparent";
  roundedRectPath(context, x + 1, y + 1, width - 2, height - 2, radius);
  context.strokeStyle = "rgba(119,101,80,.3)";
  context.lineWidth = Math.max(1, cell * 0.09);
  context.stroke();
  for (let y = 0; y < rows; y += 1) {
    for (let x = 0; x < columns; x += 1) {
      const px = outer + x * cell + cell / 2;
      const py = outer + y * cell + cell / 2;
      context.fillStyle = "rgba(91,75,59,.18)";
      context.beginPath();
      context.arc(px, py + cell * .025, Math.max(1.2, cell * 0.13), 0, Math.PI * 2);
      context.fill();
      context.fillStyle = "rgba(255,255,255,.9)";
      context.beginPath();
      context.arc(px - cell * .025, py - cell * .035, Math.max(.7, cell * 0.075), 0, Math.PI * 2);
      context.fill();
    }
  }
  context.restore();
}

function roundedRectPath(context, x, y, width, height, radius) {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  context.beginPath();
  context.moveTo(x + r, y);
  context.lineTo(x + width - r, y);
  context.quadraticCurveTo(x + width, y, x + width, y + r);
  context.lineTo(x + width, y + height - r);
  context.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  context.lineTo(x + r, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - r);
  context.lineTo(x, y + r);
  context.quadraticCurveTo(x, y, x + r, y);
  context.closePath();
}

function cssRgb(color) {
  return `rgb(${color[0] || 0},${color[1] || 0},${color[2] || 0})`;
}

function buildStats(pixels) {
  const map = new Map();
  pixels.forEach(pixel => {
    if (!pixel || pixel.empty || !pixel.code) return;
    const current = map.get(pixel.code) || { code: pixel.code, name: pixel.name || "", rgb: pixel.rgb || [190, 190, 190], count: 0 };
    current.count += 1;
    map.set(pixel.code, current);
  });
  return [...map.values()].sort((a, b) => b.count - a.count);
}

function luminance([r, g, b]) {
  return r * 0.299 + g * 0.587 + b * 0.114;
}

function isNearWhite([r, g, b]) {
  return r > 247 && g > 247 && b > 247;
}

function loadArtworkImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("作品图片读取失败，暂时无法转换 3D"));
    image.src = source;
  });
}
